import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, Order } from '../services/api';

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      const orderData = await apiService.getOrderById(orderId!);
      setOrder(orderData);
    } catch (err) {
      console.error('Failed to fetch order:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!order) return;
    
    try {
      setUpdatingStatus(true);
      await apiService.updateOrderStatus(order.id, newStatus);
      // Refresh the order data
      await fetchOrder();
    } catch (err) {
      console.error('Failed to update order status:', err);
      alert('Failed to update order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'processing': return 'bg-orange-100 text-orange-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading order details...</div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">{error || 'Order not found'}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/orders')}
              className="text-primary-600 hover:text-primary-900 mb-2 flex items-center"
            >
              ← Back to Orders
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
            <p className="text-gray-600">Order #{order.orderNumber}</p>
          </div>
          <div className="text-right">
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(order.status)}`}>
              {order.status.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Information */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Order Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Order Number:</span>
              <span className="font-medium">{order.orderNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date Created:</span>
              <span className="font-medium">{formatDate(order.createdAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Last Updated:</span>
              <span className="font-medium">{formatDate(order.updatedAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Number of Bottles:</span>
              <span className="font-medium">{order.numberOfBottles}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Price:</span>
              <span className="font-medium text-lg">€{order.totalPrice.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Country:</span>
              <span className="font-medium">{order.countryCode}</span>
            </div>
          </div>
        </div>

        {/* Status Management */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Status Management</h3>
          <div className="space-y-3">
            <div>
              <label htmlFor="status-select" className="block text-sm font-medium text-gray-700 mb-2">
                Update Status
              </label>
              <select
                id="status-select"
                value={order.status}
                onChange={(e) => handleStatusUpdate(e.target.value)}
                disabled={updatingStatus}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            {updatingStatus && (
              <div className="text-sm text-gray-600">Updating status...</div>
            )}
          </div>
        </div>

        {/* Customer Information */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h3>
          {order.customerInfo ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">{order.customerInfo.name || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{order.customerInfo.email || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Phone:</span>
                <span className="font-medium">{order.customerInfo.phone || 'N/A'}</span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No customer information available</p>
          )}
        </div>

        {/* Delivery Address */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Delivery Address</h3>
          {order.deliveryAddress ? (
            <div className="space-y-2">
              <div className="font-medium">{order.deliveryAddress.street || 'N/A'}</div>
              <div className="text-gray-600">
                {order.deliveryAddress.city && order.deliveryAddress.postalCode && 
                  `${order.deliveryAddress.city}, ${order.deliveryAddress.postalCode}`}
              </div>
              <div className="text-gray-600">{order.deliveryAddress.country || 'N/A'}</div>
            </div>
          ) : (
            <p className="text-gray-500">No delivery address available</p>
          )}
        </div>

        {/* Pickup Point */}
        <div className="card lg:col-span-2">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Wine Store Details</h3>
          {order.pickupPoint ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Store Name:</span>
                  <span className="font-medium">{order.pickupPoint.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Contact Person:</span>
                  <span className="font-medium">{order.pickupPoint.contactPerson || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{order.pickupPoint.phone || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium">{order.pickupPoint.email || 'N/A'}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="font-medium">Address:</div>
                <div className="text-gray-600">{order.pickupPoint.street || 'N/A'}</div>
                <div className="text-gray-600">
                  {order.pickupPoint.city && order.pickupPoint.postalCode && 
                    `${order.pickupPoint.city}, ${order.pickupPoint.postalCode}`}
                </div>
                <div className="text-gray-600">{order.pickupPoint.country || 'N/A'}</div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No wine store information available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
